import { useCallback, useMemo } from 'react'
import { ShieldAlert, RefreshCw } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn, RowAction } from '@/components/DataTable/types'
import { PERMISSIONS } from '@/lib/permissions'
import { StatusBadge, type StatusKind } from '@/components/StatusBadge'
import { RelativeTime } from '@/components/DataDisplay'
import { useToast } from '@/providers/UiProviders'
import { fetchUrlScans, rescanAppUrl, APP_MOD_STR as S, type UrlScanRow } from './appModApi'

/**
 * A5.19 — URL xavfsizlik tekshiruvi. GET /admin/mini-apps/url-scan (lane-A greenfield
 * → fail-soft bo'sh). Mini App URL'lari zararli/phishing skaneri natijalari; qayta
 * tekshirish → POST /admin/mini-apps/:id/rescan.
 */
export function AppUrlSecurity() {
  const { toast } = useToast()

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      try {
        await fn()
        toast(S.rescanned, { tone: 'success' })
      } catch (e) {
        toast(S.failed, { tone: 'error', ...(e instanceof Error && e.message ? { description: e.message } : {}) })
      }
    },
    [toast],
  )

  const verdictKind = (v: string): StatusKind =>
    v === 'safe' ? 'active' : v === 'malicious' ? 'banned' : v === 'suspicious' ? 'pending' : 'deleted'
  const verdictLabel = (v: string) =>
    v === 'safe' ? S.safe : v === 'malicious' ? S.malicious : v === 'suspicious' ? S.suspicious : S.unknown

  const columns = useMemo<AdminColumn<UrlScanRow>[]>(
    () => [
      { key: 'app_title', header: S.app, type: 'text', minWidth: 180 },
      {
        key: 'url',
        header: S.url,
        type: 'text',
        minWidth: 220,
        render: (r) => <span className="break-all font-mono text-xs text-secondary">{r.url}</span>,
      },
      {
        key: 'verdict',
        header: S.verdict,
        type: 'text',
        width: 140,
        render: (r) => <StatusBadge status={verdictKind(r.verdict)} label={verdictLabel(r.verdict)} />,
      },
      {
        key: 'categories',
        header: S.categories,
        type: 'text',
        minWidth: 160,
        render: (r) =>
          r.categories && r.categories.length ? (
            <div className="flex flex-wrap gap-1">
              {r.categories.map((c) => (
                <span key={c} className="rounded-sm bg-surface-3 px-1.5 py-0.5 text-xs text-secondary">
                  {c}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-tertiary">—</span>
          ),
      },
      {
        key: 'scanned_at',
        header: S.scannedAt,
        type: 'date',
        width: 150,
        render: (r) => <RelativeTime value={r.scanned_at} className="text-secondary" />,
      },
    ],
    [],
  )

  const rowActions = useMemo<RowAction<UrlScanRow>[]>(
    () => [
      {
        id: 'rescan',
        label: S.rescan,
        icon: <RefreshCw size={13} />,
        onSelect: (r) => void run(() => rescanAppUrl(r.id)),
      },
    ],
    [run],
  )

  const fetcher = useCallback(fetchUrlScans, [])

  return (
    <>
      <PageHeader title={S.urlScanTitle} screenId="A5.19" />
      <DataTable<UrlScanRow>
        screenId="A5.19"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => String(r.id)}
        rowActions={rowActions}
        exportable
        emptyState={{ icon: <ShieldAlert size={28} strokeWidth={1.5} />, title: S.scanEmpty }}
      />
    </>
  )
}
